import React, { useState, useLayoutEffect } from "react"
import { graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { BlocksRenderer } from "@strapi/blocks-react-renderer"
import YouTube from "react-youtube"
import { Cta, DOCTOR_BASE_SLUG, isBrowser } from "../libs/helpers"

import { IconExpand, IconPlay } from "../libs/icons"
import * as styles from "./video.module.scss"

const Video = ({ video, theme='basic', className, detail }) => {
  const {
    cta,
    title,
    doctor,
    eyebrow,
    youtube,
    jumplink,
    thumbnail,
    transcript,
    description,
    titleOverride,
    descriptionOverride
  } = video
  const opts = {
    playerVars: {
      autoplay: 1,
      rel: 0
    },
  }
  const img = thumbnail ? getImage(thumbnail.localFile) : undefined
  const drImg = doctor?.image ? getImage(doctor.image.localFile) : undefined

  let ytTimer = undefined
  const ytStateChange = event => {
    // console.log(event)
    if (event.data === 0) {   // end
      event.target.stopVideo()  // hide suggested next videos
      clearInterval(ytTimer)
      if (isBrowser) {
        window.dataLayer.push({ event: 'video', title: title, percent: 100 })
      }
    }
    if (event.data === 1) { // playing
      ytTimer = setInterval(() => {
        const steps = [1, 10, 25, 50, 75]
        const percent = Math.floor(event.target.getCurrentTime() / event.target.getDuration() * 100)
        if (steps.indexOf(percent) > -1) {
          if (isBrowser) {
            window.dataLayer.push({ event: 'video', title: title, percent: percent })
          }
        }
      }, 1000)
    }
    if (event.data === 2) { // paused
      clearInterval(ytTimer)
    }
  }

  let media = undefined
  if (img) {
    media = <GatsbyImage image={img} alt={img.alternativeText || 'alt'} />
  }
  if (youtube) {
    media = <YouTube videoId={youtube} opts={opts} onStateChange={ytStateChange} />
  }

  const [isOpen, setIsOpen] = useState(false)
  const [isTranscript, setIsTranscript] = useState(false)
  const modalStyles = [styles['modal']]
  if (isOpen) {
    modalStyles.push(styles['isOpen'])
  }
  const copyStyles = [styles['copy']]
  if (isTranscript) {
    copyStyles.push(styles['isOpen'])
  }

  const updateHash = ({reset = false}) => {
    if (reset) {
      window.history.pushState(null, null, window.location.pathname + window.location.search)
      closeModal()
    } else {
      window.dataLayer.push({ event: 'historyChange', hash: jumplink })
      window.location.hash = jumplink
    }
  }
  const openModal = () => {
    setIsOpen(true)
    document.body.style = 'overflow:hidden;'
  }
  const closeModal = () => {
    setIsOpen(false)
    document.body.style = ''
  }

  useLayoutEffect(() => {
    const handleKeyUp = event => {
      if (event.key === 'Escape') {
        window.history.pushState(null, null, window.location.pathname + window.location.search)
        closeModal()
      }
    }
    const handleHashChange = () => {
      window.location.hash.slice(1) === jumplink ? openModal() : closeModal()
    }
  
    // scroll to el on initial load if needed
    if (window.location.hash.slice(1) === jumplink) {
      const el = document.querySelector(`section[name=${jumplink}]`)
      el.scrollIntoView()
    }

    // listeners
    handleHashChange()
    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [jumplink])

  const classes = [styles[theme]]
  if (className) {
    classes.push(className)
  }

  const getTitle = () => {
    let t = title
    if (titleOverride && theme === 'basic') {
      t = titleOverride
    }
    return t
  }
  const getDesc = () => {
    let d = <BlocksRenderer content={description} />
    if (descriptionOverride && theme === 'basic') {
      d = descriptionOverride
    }
    return d
  }

  return (
    <section name={jumplink} className={classes.join(' ')}>
      <div className={styles['inner']}>
        <div className={styles['images']}>
          <button className={styles['poster']} onClick={updateHash}>
            <div className={styles['iconWrap']}>
              { youtube ? <IconPlay width={24} /> : <IconExpand width={24} /> }
            </div>
          { img &&
            <GatsbyImage image={img} alt={img.alternativeText || 'alt'} />
          }
          </button>
          { drImg && theme !== 'basic' && !detail &&
          <div className={styles['doctorImage']} title={doctor.name}>
            <GatsbyImage image={drImg} alt={drImg.alternativeText || 'alt'} />
          </div>
          }
        </div>
        <div className={styles['content']}>
        { eyebrow && theme !== 'basic' &&
          <div className={styles['eyebrow']}>
            {eyebrow}
          </div>
        }
        { title &&
          <h2 className={styles['title']}>
            { getTitle() }
          </h2>
        }
        { description &&
          <div className={styles['description']}>
            { getDesc() }
          </div>
        }
        { youtube && theme === 'basic' &&
          <button className={styles['play'] + ' item--link'} onClick={updateHash}>
            Play Video
          </button>
        }
        { doctor && cta && theme !== 'basic' &&
          <Cta className={styles['play'] + ' item--link'} cta={cta} />
        }
        </div>
      </div>
      <div className={modalStyles.join(' ')}>
        <div>
          <button className={styles['close']} onClick={() => {updateHash({reset:true})}}>
            <span>
              &times;
            </span>
          </button>
          <div className={styles['media']}>
            { isOpen && media &&
              media
            }
          </div>
          <div className={copyStyles.join(' ')}>
            <div>{/* initial modal panel */}
              { doctor && drImg && !detail &&
              <a href={`${DOCTOR_BASE_SLUG}/${doctor.slug}`} className={styles['doctorCta']}>
                <GatsbyImage image={drImg} alt={drImg.alternativeText || 'alt'} />
                <div>
                  <strong>{ doctor.name }</strong>
                  <p>{ doctor.job }, { doctor.branch }</p>
                </div>
              </a>
              }
              { title &&
              <div className={styles['title']}>
                {title}
              </div>
              }
              { description &&
              <div className={styles['modalDescription']}>
                <BlocksRenderer content={description} />
              </div>
              }
              { transcript &&
              <button className={styles['transcriptButton']} onClick={() => {setIsTranscript(true)}}>
                <span>&rsaquo;</span> View transcript
              </button>
              }
              {/* TODO: check conditional render logic */}
              { cta &&
              <Cta className={styles['cta'] + ' internal'} cta={cta} />
              }
            </div>
            <div>{/* transcript modal panel */}
              <div className={styles['transcriptButtonWrap']}>
                <button className={styles['transcriptButton']} onClick={() => {setIsTranscript(false)}}>
                  <span>&lsaquo;</span> Hide transcript
                </button>
              </div>
              { transcript &&
              <div className={styles['transcript']} tabIndex="0">
                <BlocksRenderer content={transcript} />
              </div>
              }
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Video

export const query = graphql`
  fragment VideoData on STRAPI__COMPONENT_COMPONENT_VIDEO {
    video {
      title
      youtube
      eyebrow
      thumbnail {
        alternativeText
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }  
      }
      titleOverride
      descriptionOverride
      transcript {
        type
        children {
          text
          type
        }
      }
      cta {
        label
        page {
          path
          title
        }
      }
      jumplink
      description {
        type
        children {
          text
          type
        }
      }
      doctor {
        branch
        image {
          alternativeText
          localFile {
            childImageSharp {
              gatsbyImageData(width: 50)
            }
          }
        }
        job
        name
        slug
      }
    }
  }
`
